import FlyoutContextProvider from "@app/shared/Flyouts/FlyoutContext";
import { ColumnConfig, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { JSX, useCallback, useEffect, useReducer } from "react";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import JobQueueService from "@app/jobs/job-queues/jobQueueService";
import { AddJobQueueModal } from "@app/jobs/job-queues/AddJobQueueModal";
import { DeleteJobQueueModal } from "@app/jobs/job-queues/DeleteJobQueueModal";
import { JobQueue } from "@app/jobs/job-queues/jobQueue";
import { JobQueueAction } from "@app/jobs/job-queues/jobQueueActions";
import { jobQueuesReducer } from "@app/jobs/job-queues/jobQueueReducer";
import { jobQueuesInitialState } from "@app/jobs/job-queues/jobQueueState";

interface JobQueuesTableData {
  [wafflesKeyColumnName]: string;
  name: string;
  workerVersionNum: number;
  memorySizeGB: number;
  fallbackJobQueue: JSX.Element;
  delete: JSX.Element;
}

const columnConfig: ColumnConfig = {
  /* oxlint-disable sort-keys */
  name: {
    displayText: "Queue Name",
    noSort: false,
    testId: "job-queues-table-queue-name",
  },
  workerVersionNum: {
    displayText: "Worker Version",
    noSort: false,
    testId: "job-queues-table-worker-version",
  },
  memorySizeGB: {
    displayText: "Memory Size (GB)",
    noSort: false,
    testId: "job-queues-table-memory-size",
  },
  fallbackJobQueue: {
    displayText: "Fallback Queue",
    noSort: false,
    testId: "job-queues-table-fallback-queue",
  },
  delete: {
    displayText: "",
    noSort: true,
    testId: "job-queues-table-delete-queue"
  }
};

const WrappedJobQueues: React.FC<React.PropsWithChildren<unknown>> = () => {
  const jobQueueService = new JobQueueService();

  const [state, dispatch] = useReducer(jobQueuesReducer, jobQueuesInitialState);

  const debouncedUpdate = useCallback(
    (queue: JobQueue) => {
      jobQueueService.updateJobQueue(queue).then(() => {
        dispatch({ type: JobQueueAction.QueueUpdated, queue });
      })
    },
    []
  );

  useEffect(() => {
    jobQueueService.getJobQueues().then((result) => {
      dispatch({ type: JobQueueAction.QueuesLoaded, result });
    });
  }, []);

  useEffect(() => {
    if (!state.updateJobQueue)
      return;

    const timeoutId = setTimeout(() => {
      debouncedUpdate(state.updateJobQueue);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [state.updateJobQueue, debouncedUpdate])

  const addJobQueue = (name: string, workerVersionNum: number, memorySizeGB: number): void => {
    jobQueueService.updateJobQueue({ id: 0, name, memorySizeGB, workerVersionNum }).then((result) => {
      dispatch({ type: JobQueueAction.QueueUpdated, queue: result })
    })
  }

  const deleteJobQueue = (id: number): void => {
    dispatch({ type: JobQueueAction.QueueDeleted, id });
    jobQueueService.deleteJobQueue(id);
  }

  const data: JobQueuesTableData[] = Object.values(state.queues).map((queue) => ({
    /* oxlint-disable sort-keys */
    [wafflesKeyColumnName]: queue.id.toString(),
    name: queue.name,
    workerVersionNum: queue.workerVersionNum,
    memorySizeGB: queue.memorySizeGB,
    fallbackJobQueue: (() => {
      return (<QESSelect
        label="Fallback Queue"
        hideLabel={true}
        onChange={(e) => {
          queue.fallbackJobQueueId = parseInt(e.target.value, 10);
          dispatch({ type: JobQueueAction.UpdateFallbackQueue, queue })
        }}
        value={queue.fallbackJobQueueId}
        disabled={false}
        testId="job-queues__edit-fallback-queue-select">
        <option key="0" value="null" disabled={false}>Select a fallback queue</option>
        {Object.values(state.queues)
          .filter(q => q.id !== queue.id &&
            (!queue.memorySizeGB || q.memorySizeGB > queue.memorySizeGB) &&
            q.workerVersionNum == queue.workerVersionNum).map((option) => (
              <option key={option.id} value={option.id}>{option.name}</option>
            ))}
      </QESSelect>)
    })() as JSX.Element,
    delete: (() => {
      return (<QESButton
        qesType="secondary-danger"
        className="pt-2 p-0"
        qesTestId="job-queues__button-delete-queue"
        onClick={() => dispatch({ type: JobQueueAction.ShowDeleteQueueModal, queue })}>
        <i className="fa fa-trash fa-lg text-danger" style={{ lineHeight: "1em" }} />
      </QESButton>)
    })() as JSX.Element
  }));

  return (
    <Page documentTitle="Job Queues - Operations">
      <Breadcrumbs template={BreadcrumbTemplateEnum.Operations} items={["Job Queues"]} />
      <PageInner>
        <PageHeader title="Job Queues" />
        <PageSection>
          <AddJobQueueModal
            confirmAdd={(queueName: string, workerVersion: number, memorySize: number) => addJobQueue(queueName, workerVersion, memorySize)}
            closeModal={() => dispatch({ type: JobQueueAction.CloseAddQueueModal })}
            isVisible={state.showAddQueueModal}
            existingQueueNames={Object.values(state.queues).map(q => q.name)}
          />
          <DeleteJobQueueModal
            id={state.deleteJobQueue?.id}
            queueName={state.deleteJobQueue?.name}
            confirmDelete={() => deleteJobQueue(state.deleteJobQueue?.id)}
            closeModal={() => dispatch({ type: JobQueueAction.CloseDeleteQueueModal })}
            isVisible={state.showDeleteQueueModal}
          />
          <Waffles<JobQueuesTableData>
            data={data}
            columns={columnConfig}
            testId=""
          />
          <QESButton
            qesTestId="job-queues_add-queue"
            qesType="secondary"
            onClick={() => dispatch({ type: JobQueueAction.ShowAddQueueModal })}>
            <i className="fa fa-plus pr5" /> Add Job Queue
          </QESButton>
        </PageSection>
      </PageInner>
    </Page>
  );
}

export const JobQueues: React.FC<unknown> = () => {
  return (
    <FlyoutContextProvider>
      <WrappedJobQueues />
    </FlyoutContextProvider>
  );
}